import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { BarChart2, TrendingUp, Globe, Users, Phone, MessageCircle, RefreshCw, Loader2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABELS = {
  nuevo: 'Nuevo', calificado: 'Calificado', pendiente_wa: 'Pendiente WA',
  contactado: 'Contactado', propuesta: 'Propuesta',
  cerrado_ganado: 'Ganado', cerrado_perdido: 'Perdido',
};
const STAGE_COLORS = {
  nuevo: '#64748b', calificado: '#3b82f6', pendiente_wa: '#f59e0b',
  contactado: '#8b5cf6', propuesta: '#10b981',
  cerrado_ganado: '#22c55e', cerrado_perdido: '#ef4444',
};
const FUNNEL_ORDER = ['nuevo','calificado','pendiente_wa','contactado','propuesta','cerrado_ganado','cerrado_perdido'];

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="glass-card p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[10px] text-text-muted uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-black ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-text-muted">{sub}</p>}
    </div>
  );
}

function MiniBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-text-muted">{label}</span>
        <span className="text-text-body font-semibold">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-card/60">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: 'easeOut' }}
          className="h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function AnalyticsDashboard({ authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiFn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFn(`${API}/analytics/dashboard`);
      setData(await res.json());
    } catch { toast.error('Error al cargar analytics'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-20 text-text-muted"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando analytics…</div>;

  const acq = data?.acquisition || {};
  const funnel = data?.funnel || {};
  const rev = data?.revenue || {};
  const byCountry = data?.by_country || [];
  const weeklyForms = data?.weekly_forms || [];
  const maxFunnelVal = Math.max(...FUNNEL_ORDER.map(s => funnel[s] || 0), 1);
  const maxWeekly = Math.max(...weeklyForms.map(w => w.count), 1);

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-primary" /> Analytics de Campañas
          </h3>
          <p className="text-sm text-text-muted mt-0.5">Métricas de adquisición, funnel y revenue en tiempo real.</p>
        </div>
        <button onClick={load} className="btn-secondary !px-3 !py-2" data-testid="analytics-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Adquisición */}
      <div>
        <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Adquisición</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Formularios" color="text-primary"
            value={acq.forms?.total ?? 0}
            sub={`+${acq.forms?.last_7d ?? 0} esta semana`} />
          <StatCard icon={MessageCircle} label="Chats" color="text-violet-400"
            value={acq.chats?.total ?? 0}
            sub={`+${acq.chats?.last_30d ?? 0} en 30 días`} />
          <StatCard icon={Phone} label="Llamadas" color="text-blue-400"
            value={acq.calls?.total ?? 0}
            sub={`+${acq.calls?.last_30d ?? 0} en 30 días`} />
          <StatCard icon={BarChart2} label="Campañas sociales" color="text-emerald-400"
            value={acq.campaigns?.total ?? 0}
            sub={`${acq.campaigns?.completed ?? 0} completadas`} />
        </div>
      </div>

      {/* Revenue */}
      <div>
        <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Revenue</p>
        <div className="grid grid-cols-3 gap-3">
          <StatCard icon={DollarSign} label="Recaudado (USD)" color="text-emerald-400" value={`$${rev.collected_usd ?? 0}`} />
          <StatCard icon={TrendingUp} label="En pipeline (USD)" color="text-amber-400" value={`$${rev.pipeline_usd ?? 0}`} />
          <StatCard icon={DollarSign} label="Total facturas" color="text-primary" value={rev.total_invoices ?? 0} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Funnel */}
        <div className="glass-card p-5 space-y-3">
          <p className="text-sm font-semibold text-text-heading">Funnel de conversión</p>
          {FUNNEL_ORDER.map(stage => (
            <MiniBar key={stage} label={STAGE_LABELS[stage]} value={funnel[stage] ?? 0}
              max={maxFunnelVal} color={STAGE_COLORS[stage]} />
          ))}
        </div>

        {/* Por país */}
        <div className="glass-card p-5 space-y-3">
          <p className="text-sm font-semibold text-text-heading flex items-center gap-2"><Globe className="w-4 h-4 text-text-muted" /> Top países</p>
          {byCountry.length === 0 ? (
            <p className="text-xs text-text-muted text-center py-4">Sin datos de países aún</p>
          ) : byCountry.map(({ country, count }, i) => (
            <MiniBar key={country} label={country || 'Desconocido'} value={count}
              max={byCountry[0]?.count || 1} color={['#6366f1','#8b5cf6','#a78bfa','#c4b5fd','#ddd6fe'][i] || '#6366f1'} />
          ))}

          {/* Formularios por semana */}
          <div className="pt-3 border-t border-border-subtle">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Formularios por semana</p>
            {weeklyForms.length === 0 ? (
              <p className="text-xs text-text-muted">Sin datos</p>
            ) : (
              <div className="flex items-end gap-1 h-16">
                {weeklyForms.map((w) => {
                  const h = Math.round((w.count / maxWeekly) * 100);
                  return (
                    <div key={w.week} className="flex-1 flex flex-col items-center gap-1" title={`Semana ${w.week}: ${w.count}`}>
                      <span className="text-[8px] text-text-muted">{w.count}</span>
                      <div className="w-full rounded-t" style={{ height: `${h}%`, backgroundColor: '#6366f1', minHeight: 2 }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
